import React, { useEffect } from 'react';
import TextInput from './fields/TextInput';
import SubmitButton from './fields/SubmitButton';
import { Box, Button, Divider, Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2'; // Grid version 2
import SelectInput from './fields/SelectInput';
import { ArrayField, useFormApi, useFormState } from 'informed';
import currencyUtil from 'utils/currency';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import AddIcon from '@mui/icons-material/Add';

export default function TransactionForm({
  isUpdate,
  loading,
  boughtAccountOptions,
  soldAccountOptions,
  onCurrencyChange,
  getCapitalOptions,
  getItemCalculation,
  getTotalCalculation,
  boughtCapitals,
  soldCapitals,
}) {
  const formState = useFormState();
  const formApi = useFormApi();

  const debtorOptions = React.useMemo(() => {
    const result = [
      {
        label: "Shop",
        value: "shop"
      },
      {
        label: "Customer",
        value: "customer"
      }
    ];

    if (isUpdate) {
      result.push({
        label: "Settle",
        value: "settle"
      });
    }
    return result;
  }, [isUpdate]);

  useEffect(() => {
    if (!formState.values.bought_currency || !formState.values.sold_currency) {
      return;
    }

    const currencyPair = `${formState.values.bought_currency}${formState.values.sold_currency}`;
    const rate = currencyUtil.getRememberExchangeRate({ currencyPair });
    if (rate) {
      formApi.setValue('sub_transactions[0].rate', rate);
    } else {
      formApi.setValue('sub_transactions[0].rate', undefined);
    }

  }, [formState.values.bought_currency, formState.values.sold_currency]);

  return (
    <>
      {/* <Debug values data initialValues /> */}
      <Grid container spacing={2}>
        <Grid xs={4.8}>
          <SelectInput
            disabled={isUpdate}
            options={currencyUtil.options}
            onChange={({ value }) => onCurrencyChange({ value, type: "bought" })}
            size="small"
            margin="none"
            field='bought_currency'
            label='Bought Currency'
          />
        </Grid>
        <Grid xs={4.8}>
          <SelectInput
            disabled={isUpdate}
            options={currencyUtil.options}
            onChange={({ value }) => onCurrencyChange({ value, type: "sold" })}
            size="small"
            margin="none"
            field='sold_currency'
            label='Sold Currency'
          />
        </Grid>
        <Grid xs={2.4}>
          <SelectInput
            size="small"
            margin="none"
            field="debtor"
            label="Debtor"
            options={debtorOptions}
          />
        </Grid>
      </Grid>
      <TextInput size="small" field="description" margin="none" label="Description" />
      <Divider />
      <Box mt={1.5} mb={2}>
        <ArrayField name="sub_transactions">
          {({ add }) => (
            <>
              <SubTransaction
                boughtAccountOptions={boughtAccountOptions}
                soldAccountOptions={soldAccountOptions}
                getCapitalOptions={getCapitalOptions}
                boughtCapitals={boughtCapitals}
                soldCapitals={soldCapitals}
                getItemCalculation={getItemCalculation}
                formState={formState}
                isUpdate={isUpdate}
              />
              <Box width={"80%"}>
                <Button disabled={isUpdate} variant="contained" color="success" sx={{ minWidth: '100%', width: '80%' }} disableElevation onClick={add}>
                  <AddIcon />
                </Button>
              </Box>
            </>
          )}
        </ArrayField>
      </Box>
      <Divider />
      <Grid container justifyContent="space-between">
        <Grid>
          <Box mt={2}>
            <Typography>Total:</Typography>
            <BoughtSoldRate
              boughtAmount={getTotalCalculation({ type: "bought", values: formState.values })}
              boughtCurrency={formState.values.bought_currency}
              soldAmount={getTotalCalculation({ type: "sold", values: formState.values })}
              soldCurrency={formState.values.sold_currency}
            />
          </Box>
        </Grid>
        <Grid>
          <SubmitButton loading={loading}>Submit</SubmitButton>
        </Grid>
      </Grid>
    </>
  );
}

const BoughtSoldRate = ({
  boughtAmount,
  boughtCurrency,
  soldAmount,
  soldCurrency,
}) => {
  return (
    <Box mb={2}>
      <Typography variant='subtitle2'>
        Bought: {boughtAmount} {boughtCurrency}
      </Typography>
      <Typography variant='subtitle2'>
        Sold: &nbsp;&nbsp;&nbsp;&nbsp; {soldAmount} {soldCurrency}
      </Typography>
      <Typography variant='subtitle2'>
        Rate: &nbsp;&nbsp;&nbsp;&nbsp; {currencyUtil.calculateExchnageRate({ boughtCurrency, soldCurrency, boughtAmount, soldAmount })}
      </Typography>
    </Box>
  );
};

const SubTransaction = ({
  boughtAccountOptions,
  soldAccountOptions,
  getCapitalOptions,
  boughtCapitals,
  soldCapitals,
  getItemCalculation,
  isUpdate,
  formState
}) => {
  return (
    <ArrayField.Items>
      {({ index, remove, getValue, setValue, getData }) => {
        const calculateOnChange = ({ value, type }) => {
          const fillOn = getValue('fill_on');

          if (!fillOn) {
            return;
          }

          // prevent 2 fields to trigger (Maximum call stack size exceeded error)
          if (type !== fillOn && type !== "rate") return;

          const targetField = `${fillOn === 'sold' ? 'bought' : 'sold'}_quantity`;

          let rate, fromQuantity;
          if (type === 'rate') {
            rate = value;
            fromQuantity = getValue(`${fillOn}_quantity`);
          } else {
            rate = getValue('rate');
            fromQuantity = value;
          }

          if (rate === undefined || fromQuantity === undefined) {
            setValue(targetField, undefined);
            return;
          }

          const boughtCapitalId = getValue('bought_capital_id');
          const soldCapitalId = getValue('sold_capital_id');

          const boughtCapital = boughtCapitals.filter((capital) => (capital.capitalId === boughtCapitalId))[0];
          const soldCapital = soldCapitals.filter((capital) => (capital.capitalId === soldCapitalId))[0];

          if (!boughtCapital?.worth || !soldCapital?.worth) {
            setValue(targetField, undefined);
            return;
          }

          let result = 0;
          // fill on sold (calculate bought)
          if (fillOn === "sold") {
            const amount = fromQuantity * soldCapital.worth;
            result = currencyUtil.calculateResult({
              rate,
              fromAmount: amount,
              fromCapital: soldCapital,
              toCapital: boughtCapital,
            });
          }


          // fill on bought (calculate sold)
          if (fillOn === "bought") {
            const amount = fromQuantity * boughtCapital.worth;
            result = currencyUtil.calculateResult({
              rate,
              fromAmount: amount,
              fromCapital: boughtCapital,
              toCapital: soldCapital,
            });
          }

          setValue(targetField, result);
        };


        const setDefaultCapitalId = ({ type }) => {
          // !TODO find a better way to do this
          setTimeout(() => {
            const accountId = getValue(`${type}_account_id`);
            const capitalId = getValue(`${type}_capital_id`);
            if (!accountId || capitalId) return;
            const firstOption = getCapitalOptions({
              accountId,
              type
            })[0];
            setValue(`${type}_capital_id`, firstOption.value);
          }, 200);
        };

        const previousSubTransaction = formState.values.sub_transactions && formState.values.sub_transactions[index - 1];

        return (
          <>
            {index > 0 && <Divider variant='middle' />}
            <Grid container justifyContent="space-between" mt={1}>
              <Grid container xs={9.6} spacing={2}>
                <Grid xs={6}>
                  <Typography variant='subtitle1' mb={1}>Bought - ယူ</Typography>
                  <SelectInput
                    disabled={
                      !formState.values.bought_currency ||
                      !formState.values.sold_currency ||
                      isUpdate
                    }
                    resetOnChange={formState.initialValues.bought_currency !== formState.values.bought_currency}
                    size="small"
                    margin="none"
                    field='bought_account_id'
                    label='Bought Account'
                    options={boughtAccountOptions}
                    onChange={() => { setDefaultCapitalId({ type: 'bought' }); }}
                  />
                  <SelectInput
                    disabled={!getValue('bought_account_id') || isUpdate}
                    resetOnChange={formState.initialValues.bought_currency !== formState.values.bought_currency}
                    size="small"
                    margin="none"
                    field='bought_capital_id'
                    label='Bought Capital'
                    options={getCapitalOptions({
                      accountId: getValue('bought_account_id'),
                      type: "bought"
                    })}
                  />
                  <TextInput
                    disabled={
                      !getValue('bought_account_id') ||
                      !getValue('bought_capital_id') ||
                      (getValue('fill_on') === 'sold') ||
                      isUpdate
                    }
                    type='number'
                    size="small"
                    margin="none"
                    field="bought_quantity"
                    label="Bought Quantity"
                    onChange={({ value }) => calculateOnChange({ value, type: "bought" })}
                    resetDependOn={formState.values.bought_currency}
                  />
                  <BoughtSoldRate
                    disabled={isUpdate}
                    boughtAmount={getItemCalculation({
                      capitalId: getValue('bought_capital_id'),
                      quantity: getValue('bought_quantity'),
                      type: 'bought',
                    })}
                    boughtCurrency={formState.values.bought_currency}
                    soldAmount={getItemCalculation({
                      capitalId: getValue('sold_capital_id'),
                      quantity: getValue('sold_quantity'),
                      type: 'sold',
                    })}
                    soldCurrency={formState.values.sold_currency}
                  />
                </Grid>
                <Grid xs={6}>
                  <Typography variant='subtitle1' mb={1}>Sold - ပေး</Typography>
                  <SelectInput
                    disabled={
                      !formState.values.sold_currency ||
                      !formState.values.bought_currency ||
                      isUpdate
                    }
                    resetOnChange={formState.initialValues.sold_currency !== formState.values.sold_currency}
                    size="small"
                    margin="none"
                    field='sold_account_id'
                    label='Sold Account'
                    options={soldAccountOptions}
                    onChange={() => { setDefaultCapitalId({ type: 'sold' }); }}
                  />
                  <SelectInput
                    disabled={!getValue('sold_account_id') || isUpdate}
                    resetOnChange={formState.initialValues.sold_currency !== formState.values.sold_currency}
                    size="small"
                    margin="none"
                    field='sold_capital_id'
                    label='Sold Capital'
                    options={getCapitalOptions({
                      accountId: getValue('sold_account_id'),
                      type: "sold"
                    })}
                  />
                  <TextInput
                    disabled={
                      !getValue('sold_account_id') ||
                      !getValue('sold_capital_id') ||
                      (getValue('fill_on') === 'bought') ||
                      isUpdate
                    }
                    onChange={({ value }) => calculateOnChange({ value, type: "sold" })}
                    resetDependOn={formState.values.sold_currency}
                    type='number'
                    size="small"
                    margin="none"
                    field="sold_quantity"
                    label="Sold Quantity"
                  />
                </Grid>
              </Grid>
              <Grid xs={2}>
                <Typography variant='subtitle1' mb={1}>&nbsp;</Typography>
                <SelectInput
                  size="small"
                  margin="none"
                  field="fill_on"
                  label="Fill On"
                  initialValue={previousSubTransaction?.fill_on}
                  options={[
                    {
                      label: "Bought",
                      value: "bought"
                    },
                    {
                      label: "Sold",
                      value: "sold"
                    },
                  ]}
                />
                <TextInput
                  field='rate'
                  size="small"
                  label="Rate"
                  type='text'
                  margin="none"
                  initialValue={previousSubTransaction?.rate}
                  disabled={
                    !formState.values.bought_currency ||
                    !formState.values.sold_currency ||
                    isUpdate
                  }
                  onChange={({ value }) => calculateOnChange({ value, type: 'rate' })}
                />
                <Button disabled={isUpdate} variant="contained" color="error" sx={{ minWidth: '100%', width: '100%' }} disableElevation onClick={remove}>
                  <DeleteForeverOutlinedIcon />
                </Button>
                <Divider />
              </Grid>
            </Grid>
          </>
        );
      }}
    </ArrayField.Items>
  );
};
